import * as React from 'react';
import { Module } from 'owa-workloads';
import { observer } from 'mobx-react-lite';
import AppBarControl from '@office-start/appbar-control';
import { PlatformAppCommands } from '@office-start/appbar-control/dist/PlatformAppCommands';
import AccountSwitcher from 'owa-account-switcher';
import { isFeatureEnabled } from 'owa-feature-flags';
import { MultiAccountSwitcher } from 'owa-multi-account-switcher';
import { isMultiAccountScenario } from 'owa-left-rail-utils/lib/isMultiAccountScenario';
import { isCloudCacheScenario } from 'owa-left-rail-utils/lib/isCloudCacheScenario';
import { isEasiIdUser } from 'owa-cloud-cache-accounts-option';
import { FluentProvider } from '@fluentui/react-components';
import { getApolloClient } from 'owa-apollo';
import { AboutDialog, createAboutDialog } from 'owa-app-host-components';
import styles from './AppBar.scss';
import { getPalette } from 'owa-theme';
import { isCurrentCultureRightToLeft } from 'owa-localize';
import { createFluentTheme } from '../utils/createFluentTheme';

export const { isAboutDialogOpen, onOpenAboutDialog, taskModuleProps } = createAboutDialog();

export interface AppBarProps extends React.HTMLAttributes<HTMLDivElement> {
    selectedModule: Module;
    /**
     * The action to call when the item is clicked and it also represents the active module.
     * - (eg, clicking mail while in mail)
     */
    activeModuleAction?: (
        ev?: React.MouseEvent<unknown> | React.KeyboardEvent<HTMLElement>
    ) => void;
}

export default observer(function WrappedAppBar(props: AppBarProps) {
    const isCloudCacheMultiAccount =
        isCloudCacheScenario() || isMultiAccountScenario() || isEasiIdUser();

    React.useEffect(() => {
        const cache = getApolloClient().cache;
        /*
         * We know that this runs after AppBar is rendered --hence made its first query
         * call to populate its tile data. Therefore, we now need to stop reading from cache
         * and start reading from local resolver link, which is the source-of-truth for AppBar's state
         */
        cache.modify({
            id: cache.identify({ __typename: 'Query' }),
            fields: {
                m365Apps(_valuesInCache, { DELETE }) {
                    return DELETE;
                },
            },
        });
    }, []);

    return (
        <>
            {isAboutDialogOpen() ? <AboutDialog {...taskModuleProps} /> : undefined}
            <div className={styles.leftRail}>
                {isCloudCacheMultiAccount && (
                    <div className={styles.multiAccountContainer}>
                        {isFeatureEnabled('auth-cloudCache') && <AccountSwitcher />}
                        {isMultiAccountScenario() && props.selectedModule == Module.Mail && (
                            <MultiAccountSwitcher activeAccountAction={props.activeModuleAction} />
                        )}
                    </div>
                )}
                <FluentProvider
                    theme={createFluentTheme(getPalette())}
                    style={{ backgroundColor: 'transparent', height: '100%' }}
                    dir={isCurrentCultureRightToLeft() ? 'rtl' : 'ltr'}>
                    <AppBarControl
                        transparent={true}
                        platformAppCommands={PlatformAppCommands.About}
                        animateSelection={true}
                    />
                </FluentProvider>
            </div>
        </>
    );
});
